<script setup lang="ts">
import { ref, computed } from 'vue';
import { ActiveOpenChat, VerifyChatOpenDto } from '@/common/api/models.pleased';
import * as PleasedApi from '@/common/api/api.pleased';
import PlsDialog from '@/components/PlsDialog.vue';
import type { PlsDialogAction } from '@/components/PlsDialog.types';
import ImBubbles2Icon from '@/components/icons/ImBubbles2Icon.vue';
import TruncatedText from '@/components/TruncatedText.vue';
import MdReplay from '@/components/icons/MdReplay.vue';
import MdQuestionMark from '@/components/icons/MdQuestionMark.vue';
import HiDotsHorizontal from '@/components/icons/HiDotsHorizontal.vue';
import * as Locale from '@/plugins/i18n';
import { TranslateResult } from 'vue-i18n';
import { BTN_EVTS } from '@/directives/trackClick';
import { PAGE_EVENTS } from '@/mixins/logPageEvent';
import { analytics } from '@/common/logs/EventLogger';
import { useModalCustomisation } from '@/common/customisation/composables/useModalCustomisation';
import { createTextStyle } from '@/common/customisation/utils/styleUtils';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow';
import { devLog } from '@/common/debug';

interface ConfirmResumableChatProps {
  resumableChat: ActiveOpenChat;
}

interface ConfirmResumableChatEmits {
  (e: 'cancel', resumableChat: ActiveOpenChat): void;
  (e: 'newChat', resumableChat: ActiveOpenChat): void;
  (e: 'activeConversation', resumableChat: ActiveOpenChat): void;
  (e: 'myRequests', resumableChat: ActiveOpenChat): void;
  (e: 'resume', chat: ActiveOpenChat, canBeResumed: VerifyChatOpenDto): void;
}

interface ModalConfig {
  title: TranslateResult;
  icon: any;
  message?: { text: string | null; truncate?: boolean; html?: boolean };
  buttons: PlsDialogAction[];
  fullWidth?: boolean;
}

enum Route {
  ConfirmResumableChat = 'ConfirmResumableChat',
  ClosedActiveConversationChat = 'ClosedActiveConversationChat',
  ClosedMyRequestChat = 'ClosedMyRequestChat',
}

const RESUMABLE_CHAT_EVENTS = {
  resume: 'resume',
  cancel: 'cancel',
  activeConversation: 'active-conversation',
  newChat: 'new-chat',
  myRequests: 'my-requests',
} as const;

type ResumableChatEvent = (typeof RESUMABLE_CHAT_EVENTS)[keyof typeof RESUMABLE_CHAT_EVENTS];

const MODAL_ROUTE_PAGE_EVENT_MAP = {
  [Route.ConfirmResumableChat]: PAGE_EVENTS.MODAL_CONFIRM_RESUMABLE_CHAT,
  [Route.ClosedActiveConversationChat]: PAGE_EVENTS.MODAL_CLOSED_AC_CHAT,
  [Route.ClosedMyRequestChat]: PAGE_EVENTS.MODAL_CLOSED_MY_REQUEST_CHAT,
} as const;

const MODAL_EVENTS_TO_CLICK_TRACK_EVENTS_MAP = {
  [Route.ConfirmResumableChat]: {
    [RESUMABLE_CHAT_EVENTS.resume]: BTN_EVTS.MODAL_CONFIRM_RESUMABLE_CHAT_CONTINUE,
    [RESUMABLE_CHAT_EVENTS.cancel]: BTN_EVTS.MODAL_CONFIRM_RESUMABLE_CHAT_END_CHAT,
  },
  [Route.ClosedActiveConversationChat]: {
    [RESUMABLE_CHAT_EVENTS.activeConversation]: BTN_EVTS.MODAL_CLOSED_AC_CHAT_GO_TO_AC,
    [RESUMABLE_CHAT_EVENTS.newChat]: BTN_EVTS.MODAL_CLOSED_AC_CHAT_NEW_CONVERSATION,
  },
  [Route.ClosedMyRequestChat]: {
    [RESUMABLE_CHAT_EVENTS.myRequests]: BTN_EVTS.MODAL_CLOSED_MY_REQUEST_CHAT_GO_TO_MY_REQUESTS,
    [RESUMABLE_CHAT_EVENTS.newChat]: BTN_EVTS.MODAL_CLOSED_MY_REQUEST_CHAT_NEW_CONVERSATION,
  },
} as const;

const props = defineProps<ConfirmResumableChatProps>();
const emit = defineEmits<ConfirmResumableChatEmits>();

// Get customisation and mocked flow store
const customisation = useModalCustomisation();
const mockedFlowStore = useMockedFlowStore();

// State
const loading = ref(false);
const route = ref<Route>(Route.ConfirmResumableChat);
const resolvedTicketId = ref<null | number>(null);

// Computed styles
const iconPrimaryStyle = computed(() => createTextStyle(customisation.header.icon.color));

const iconSecondaryStyle = computed(() => createTextStyle(customisation.container.backgroundColor));

// Computed properties
const description = computed(() => {
  const subject = props.resumableChat?.subject?.trim();
  return subject;
});

const btn = (
  color: 'primary' | 'secondary' | 'text',
  text: string | TranslateResult,
  action: () => void,
  testId?: string
): PlsDialogAction => ({
  id: `${color}-${text}`,
  text: String(text),
  color,
  action,
  testId,
});

const modal = computed<ModalConfig>(() => {
  const createEventHandler = (event: ResumableChatEvent) => () => {
    const modalEventsMap = MODAL_EVENTS_TO_CLICK_TRACK_EVENTS_MAP[route.value] as Record<ResumableChatEvent, string>;

    if (modalEventsMap && modalEventsMap[event]) {
      analytics.trackClick({
        name: modalEventsMap[event],
        selection: props.resumableChat.id,
      });
    }

    emit(
      event as any,
      !props.resumableChat.ticketId ? { ...props.resumableChat, ticketId: resolvedTicketId.value } : props.resumableChat
    );
  };

  const RouteModal = {
    [Route.ConfirmResumableChat]: () => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(description.value ?? '', 'text/html');

      return {
        title: Locale.i18n.t('page.resumable_chat.confirm.title'),
        icon: HiDotsHorizontal,
        message: { text: doc.documentElement.textContent, truncate: true },
        buttons: [
          btn(
            'text',
            Locale.i18n.t('page.resumable_chat.confirm.btn.continue'),
            checkIfChatIsResumable,
            'confirm-resumable-chat-continue-button'
          ),
          btn('primary', Locale.i18n.t('global.end_chat'), createEventHandler('cancel')),
        ],
      };
    },
    [Route.ClosedActiveConversationChat]: () => ({
      title: Locale.i18n.t('page.resumable_chat.cannot_resume.title'),
      icon: MdReplay,
      message: {
        text: `${Locale.i18n.t(
          'page.resumable_chat.cannot_resume.active_conversation_message_1'
        )}<br><br>${Locale.i18n.t('page.resumable_chat.cannot_resume.active_conversation_message_2')}`,
        html: true,
      },
      buttons: [
        btn(
          'primary',
          Locale.i18n.t('page.resumable_chat.confirm.btn.go_to_ac'),
          createEventHandler(RESUMABLE_CHAT_EVENTS.activeConversation)
        ),
        btn(
          'secondary',
          Locale.i18n.t('page.active_conversation.new'),
          createEventHandler(RESUMABLE_CHAT_EVENTS.newChat)
        ),
      ],
      fullWidth: true,
    }),
    [Route.ClosedMyRequestChat]: () => ({
      title: Locale.i18n.t('page.resumable_chat.cannot_resume.title'),
      icon: MdQuestionMark,
      message: {
        text: `${Locale.i18n.t('page.resumable_chat.cannot_resume.my_requests_message_1')}<br><br>${Locale.i18n.t(
          'page.resumable_chat.cannot_resume.my_requests_message_2'
        )}`,
        html: true,
      },
      buttons: [
        btn(
          'primary',
          Locale.i18n.t('page.resumable_chat.confirm.btn.go_to_my_request'),
          createEventHandler(RESUMABLE_CHAT_EVENTS.myRequests)
        ),
        btn(
          'secondary',
          Locale.i18n.t('page.active_conversation.new'),
          createEventHandler(RESUMABLE_CHAT_EVENTS.newChat)
        ),
      ],
      fullWidth: true,
    }),
  };

  return RouteModal[route.value]?.();
});

// Methods
async function checkIfChatIsResumable() {
  if (loading.value) return;
  loading.value = true;

  // Use mocked data if in mocked mode
  let canBeResumed: VerifyChatOpenDto;

  if (mockedFlowStore.isActive && mockedFlowStore.verifyChatOpen) {
    devLog.debug('[RESUMABLE_CHAT] Using mocked verify data');
    canBeResumed = mockedFlowStore.verifyChatOpen;
    loading.value = false;
  } else {
    canBeResumed = await PleasedApi.checkChatCanBeResumed({ chatId: props.resumableChat.id })
      .catch(() => ({ canResume: false, inActiveConversation: false, ticketId: null }))
      .finally(() => (loading.value = false));
  }

  if (canBeResumed.canResume) {
    analytics.trackClick({
      name: BTN_EVTS.MODAL_CONFIRM_RESUMABLE_CHAT_CONTINUE,
      selection: props.resumableChat.id,
    });
    return emit('resume', props.resumableChat, canBeResumed);
  }

  resolvedTicketId.value = canBeResumed.ticketId || null;

  // Track page close + new enter event here since we switch to another modal route.
  analytics.trackPageEvent({
    name: PAGE_EVENTS.MODAL_CONFIRM_RESUMABLE_CHAT,
    enter: false,
    selection: props.resumableChat.id,
  });

  route.value = canBeResumed.inActiveConversation ? Route.ClosedActiveConversationChat : Route.ClosedMyRequestChat;

  analytics.trackPageEvent({
    name: MODAL_ROUTE_PAGE_EVENT_MAP[route.value],
    enter: true,
    selection: props.resumableChat.id,
  });
}

// Track page entry on mount
analytics.trackPageEvent({
  name: MODAL_ROUTE_PAGE_EVENT_MAP[route.value],
  enter: true,
  selection: props.resumableChat.id,
});
</script>

<!-- eslint-disable vue/no-v-html -->
<template>
  <PlsDialog
    v-if="modal"
    :open="true"
    :title="String(modal.title)"
    :actions="modal.buttons"
    :full-width="modal.fullWidth"
  >
    <template #icon>
      <div class="icon-container">
        <ImBubbles2Icon class="chat-bubble-icon" :style="iconPrimaryStyle" height="10em" width="10em" />
        <component
          :is="modal.icon"
          class="chat-bubble-icon-secondary"
          :style="iconSecondaryStyle"
          height="5em"
          width="5em"
        />
      </div>
    </template>

    <template #default>
      <p v-if="modal.message" class="message-text theme-font-main">
        <template v-if="modal.message.truncate">
          "<TruncatedText :maxlength="100" :text="modal.message.text" class="theme-font-main" />"
        </template>
        <span v-else v-html="modal.message.text" />
      </p>
    </template>
  </PlsDialog>
</template>

<style lang="scss" scoped>
.icon-container {
  position: relative;
  display: inline-block;
  text-align: center;
  margin-bottom: 1rem;

  .chat-bubble-icon {
    display: block;
  }

  .chat-bubble-icon-secondary {
    position: absolute;
    top: 1.5em;
    left: 1.5em;
  }
}

.message-text {
  text-align: center;
  margin: 1rem 0;
}
</style>
